import React, { useMemo, useState } from "react";
import {
  FaBuilding,
  FaClipboard,
  FaEnvelope,
  FaGlobe,
  FaInfoCircle,
  FaMagic,
  FaMapMarkerAlt,
  FaPhone,
  FaSave,
  FaStickyNote,
  FaUserTie,
} from "react-icons/fa";
import { Input, Modal, Select, Textarea } from "./ui";
import { SITE_STATUS, STATUTS_CANON } from "../constants";
import { inputDateTimeToSql, parseEmails, toInputDateTime } from "../utils";

function normalizeRawText(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function unique(values) {
  return Array.from(
    new Set(values.map((v) => String(v || "").trim()).filter(Boolean)),
  );
}

function formatFrenchPhone(value) {
  if (!value) return "";

  const raw = String(value).trim();

  if (raw.startsWith("+33")) {
    return raw.replace(/\s+/g, " ");
  }

  const digits = raw.replace(/\D/g, "");

  if (digits.length !== 10) {
    return raw.replace(/\s+/g, " ");
  }

  return digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
}

function getDepartmentFromPostalCode(postalCode) {
  if (!postalCode) return "";

  const clean = String(postalCode).trim();

  if (/^(97|98)\d{3}$/.test(clean)) {
    return clean.slice(0, 3);
  }

  return clean.slice(0, 2);
}

function cleanCommuneName(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .replace(/^mairie\s*déléguée\s*[-–—]?\s*/i, "")
    .replace(/^mairie\s*deleguee\s*[-–—]?\s*/i, "")
    .replace(/^mairie\s*annexe\s*[-–—]?\s*/i, "")
    .replace(/^mairie\s*[-–—]?\s*/i, "")
    .replace(/^commune\s*[-–—]?\s*/i, "")
    .replace(/^de\s+/i, "")
    .replace(/[.,;:]+$/g, "")
    .trim();
}

function parseServicePublicMairieText(rawValue) {
  const text = normalizeRawText(rawValue);
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const fullText = lines.join("\n");

  const mairieLine = lines.find((line) =>
    /^(Mairie|Mairie annexe|Mairie déléguée|Mairie deleguee|Commune)\s*[-–—]\s*.+/i.test(
      line,
    ),
  );

  const commune = cleanCommuneName(mairieLine || "");

  const emails = unique(
    fullText.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [],
  ).map((email) => email.toLowerCase());

  const phoneMatch = fullText.match(/(?:\+33\s?|0)[1-9](?:[\s.-]?\d{2}){4}/);

  const telephone = phoneMatch ? formatFrenchPhone(phoneMatch[0]) : "";

  const urls = unique(fullText.match(/https?:\/\/[^\s)]+/gi) || []).map((url) =>
    url.replace(/[.,;]+$/g, ""),
  );

  const siteUrl =
    urls.find(
      (url) =>
        !/service-public\.gouv\.fr/i.test(url) &&
        !/legifrance\.gouv\.fr/i.test(url) &&
        !/info\.gouv\.fr/i.test(url) &&
        !/data\.gouv\.fr/i.test(url) &&
        !/vie-publique\.fr/i.test(url),
    ) || "";

  const breadcrumbDeptMatch = fullText.match(
    /([A-ZÀ-Ÿa-zà-ÿ'’\-\s]+)\s*-\s*(\d{2,3})\s*(?:Mairie|$)/i,
  );

  const postalCodeMatch = fullText.match(/\b(\d{5})\b/);

  const departementCode = breadcrumbDeptMatch?.[2]
    ? breadcrumbDeptMatch[2]
    : postalCodeMatch?.[1]
      ? getDepartmentFromPostalCode(postalCodeMatch[1])
      : "";

  return {
    commune,
    nom_club: commune || "",
    discipline: "Mairie",
    departement_code: departementCode,
    email: emails.join("; "),
    telephone,
    site_url: siteUrl,
    site_status: "",
    statut: "",
  };
}

export default function ProspectionModal({
  title,
  initial = {},
  prefill = {},
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState({
    nom_club: initial.nom_club || "",
    discipline: initial.discipline || prefill.discipline || "",
    departement_code:
      initial.departement_code || prefill.departement_code || "",
    nb_licencies: initial.nb_licencies ?? "",
    email: initial.email || "",
    telephone: initial.telephone || "",
    contact_nom: initial.contact_nom || "",
    fonction: initial.fonction || "",
    statut: initial.statut || "",
    date_contact: initial.date_contact
      ? toInputDateTime(initial.date_contact)
      : "",
    site_url: initial.site_url || "",
    site_status: initial.site_status || "",
    facebook_url: initial.facebook_url || "",
    observations: initial.observations || "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [importText, setImportText] = useState("");
  const [importPreview, setImportPreview] = useState(null);

  const emails = useMemo(() => parseEmails(form.email || ""), [form.email]);

  const handle = (k, v) => {
    setError("");
    setForm((s) => ({ ...s, [k]: v }));
  };

  const handleServicePublicImport = () => {
    const parsed = parseServicePublicMairieText(importText);

    const hasUsefulData =
      parsed.nom_club ||
      parsed.email ||
      parsed.telephone ||
      parsed.site_url ||
      parsed.departement_code;

    if (!hasUsefulData) {
      setError(
        "Aucune donnée exploitable détectée. Vérifie que le texte collé contient bien une fiche mairie Service-Public.",
      );
      return;
    }

    setError("");
    setImportPreview(parsed);

    setForm((current) => ({
      ...current,
      nom_club: parsed.nom_club || current.nom_club,
      discipline: parsed.discipline || current.discipline || "Mairie",
      departement_code: parsed.departement_code || current.departement_code,
      email: parsed.email || current.email,
      telephone: parsed.telephone || current.telephone,
      site_url: parsed.site_url || current.site_url,

      // On laisse volontairement ces champs vides.
      site_status: current.site_status || "",
      statut: current.statut || "",

      fonction: current.fonction || "Mairie",
    }));
  };

  const clearServicePublicImport = () => {
    setImportText("");
    setImportPreview(null);
    setError("");
  };

  const submit = async () => {
    if (!form.nom_club.trim()) {
      setError("Le nom du club est obligatoire.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        nom_club: form.nom_club.trim(),
        discipline: form.discipline.trim() || null,
        departement_code: form.departement_code.trim() || null,
        nb_licencies:
          form.nb_licencies !== "" && form.nb_licencies !== null
            ? Number(form.nb_licencies)
            : null,
        email: form.email.trim() || null,
        telephone: form.telephone.trim() || null,
        contact_nom: form.contact_nom.trim() || null,
        fonction: form.fonction.trim() || null,
        statut: form.statut || null,
        date_contact: form.date_contact
          ? inputDateTimeToSql(form.date_contact)
          : null,
        site_url: form.site_url.trim() || null,
        site_status: form.site_status || null,
        facebook_url: form.facebook_url.trim() || null,
        observations: form.observations.trim() || null,
      };

      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={title}>
      <div className="space-y-5">
        <div className="rounded-3xl bg-slate-950 text-white overflow-hidden">
          <div className="p-5 bg-[radial-gradient(circle_at_top_left,#2563eb,transparent_35%),radial-gradient(circle_at_bottom_right,#7c3aed,transparent_30%)]">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs text-white/85">
              <FaInfoCircle />
              Fiche prospect
            </div>

            <h3 className="mt-3 text-2xl font-bold">
              {form.nom_club || "Nouveau prospect"}
            </h3>

            <p className="mt-2 text-sm text-white/70">
              Renseignez les informations utiles pour qualifier le contact,
              suivre les échanges et préparer vos campagnes.
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl bg-rose-50 text-rose-700 border border-rose-100 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <Section
          title="Import rapide depuis Service-Public"
          icon={<FaClipboard />}
        >
          <div className="space-y-3">
            <Textarea
              label="Coller ici le texte complet de la fiche mairie"
              value={importText}
              onChange={(v) => setImportText(v)}
              rows={7}
              placeholder="Ex : Mairie - Wavignies, Contacts, Site web, Adresse électronique, Téléphone..."
            />

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Le texte sera analysé pour récupérer automatiquement la commune,
                les emails, le téléphone, le site web et le département.
              </p>

              <div className="flex flex-col sm:flex-row gap-2">
                {importText && (
                  <button
                    type="button"
                    onClick={clearServicePublicImport}
                    className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm"
                  >
                    Effacer
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleServicePublicImport}
                  disabled={!importText.trim()}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <FaMagic />
                  Pré-remplir avec ce texte
                </button>
              </div>
            </div>

            {importPreview && (
              <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200">
                <div className="font-semibold mb-1">Données détectées :</div>

                <div className="space-y-1">
                  {importPreview.nom_club && (
                    <div>
                      <span className="font-medium">Prospect :</span>{" "}
                      {importPreview.nom_club}
                    </div>
                  )}

                  {importPreview.departement_code && (
                    <div>
                      <span className="font-medium">Département :</span>{" "}
                      {importPreview.departement_code}
                    </div>
                  )}

                  {importPreview.email && (
                    <div>
                      <span className="font-medium">Email(s) :</span>{" "}
                      {importPreview.email}
                    </div>
                  )}

                  {importPreview.telephone && (
                    <div>
                      <span className="font-medium">Téléphone :</span>{" "}
                      {importPreview.telephone}
                    </div>
                  )}

                  {importPreview.site_url && (
                    <div>
                      <span className="font-medium">Site :</span>{" "}
                      {importPreview.site_url}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Section>

        <Section title="Identification du club" icon={<FaBuilding />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Nom du club *"
              value={form.nom_club}
              onChange={(v) => handle("nom_club", v)}
              required
            />

            <Input
              label="Discipline"
              value={form.discipline}
              onChange={(v) => handle("discipline", v)}
              placeholder="Judo, Karaté, Mairie, Multisport…"
            />

            <Input
              label="Département / Zone"
              value={form.departement_code}
              onChange={(v) => handle("departement_code", v)}
              placeholder="33, 75, BE-BRU…"
              icon={<FaMapMarkerAlt />}
            />

            <Input
              label="Nb licenciés"
              type="number"
              value={form.nb_licencies}
              onChange={(v) => handle("nb_licencies", v)}
              placeholder="Ex : 120"
            />
          </div>
        </Section>

        <Section title="Coordonnées" icon={<FaUserTie />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Contact principal"
              value={form.contact_nom}
              onChange={(v) => handle("contact_nom", v)}
              placeholder="Nom du président, secrétaire…"
            />

            <Input
              label="Fonction"
              value={form.fonction}
              onChange={(v) => handle("fonction", v)}
              placeholder="Président, enseignant, mairie…"
            />

            <Input
              label="Email(s)"
              value={form.email}
              onChange={(v) => handle("email", v)}
              placeholder="contact@club.fr; president@club.fr"
              icon={<FaEnvelope />}
              className="md:col-span-2"
            />

            <div className="md:col-span-2 -mt-1 text-xs text-slate-500">
              {emails.length > 0
                ? `${emails.length} email(s) détecté(s) : ${emails.join(", ")}`
                : "Vous pouvez saisir plusieurs emails séparés par ; ou ,"}
            </div>

            <Input
              label="Téléphone"
              value={form.telephone}
              onChange={(v) => handle("telephone", v)}
              placeholder="06..."
              icon={<FaPhone />}
            />
          </div>
        </Section>

        <Section title="Qualification & suivi" icon={<FaStickyNote />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Statut"
              value={form.statut}
              onChange={(v) => handle("statut", v)}
              options={[
                { label: "—", value: "" },
                ...STATUTS_CANON.map((s) => ({ label: s, value: s })),
              ]}
            />

            <Input
              label="Date de contact"
              type="datetime-local"
              value={form.date_contact}
              onChange={(v) => handle("date_contact", v)}
            />

            <Select
              label="Statut du site"
              value={form.site_status}
              onChange={(v) => handle("site_status", v)}
              options={[
                { label: "—", value: "" },
                ...SITE_STATUS.map((s) => ({ label: s, value: s })),
              ]}
            />
          </div>
        </Section>

        <Section title="Présence web & notes" icon={<FaGlobe />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Site URL"
              value={form.site_url}
              onChange={(v) => handle("site_url", v)}
              placeholder="https://..."
              icon={<FaGlobe />}
            />

            <Input
              label="Facebook URL"
              value={form.facebook_url}
              onChange={(v) => handle("facebook_url", v)}
              placeholder="https://facebook.com/..."
            />

            <Textarea
              label="Observations"
              value={form.observations}
              onChange={(v) => handle("observations", v)}
              rows={5}
              className="md:col-span-2"
              placeholder="Historique, remarques, prochaine action, besoins détectés…"
            />
          </div>
        </Section>

        <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            Annuler
          </button>

          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950 hover:opacity-90 disabled:opacity-50"
          >
            <FaSave />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="flex items-center gap-2 font-semibold text-slate-900 dark:text-slate-100 mb-4">
        <span className="text-slate-400">{icon}</span>
        {title}
      </div>

      {children}
    </section>
  );
}
